/* nimbus-motion-reveals.js
 * Editorial-beauty motion vocabulary for Sugar Browed.
 *
 * Six primitives used with discipline — never apply more than 2-3 per section.
 *   wordMask     — split heading into words, mask-reveal each word from below
 *   letterTrack  — eyebrows: tighten letter-spacing from wide → final
 *   imageReveal  — clip-path mask + inner counter-translate / scale-settle
 *   staggerDepth — cards: cascading y + scale + subtle ±rotation
 *   scalePop     — numbers/badges: 0.85 → 1 with elastic-ish back ease
 *   magneticTilt — pointer-driven 3D tilt for cards (idempotent, hover-only)
 *
 * Plus utility helpers:
 *   ctxClean     — wraps onComplete to strip will-change / inline transform
 *   stOnce       — shared ScrollTrigger config factory
 *   isReduced    — prefers-reduced-motion guard
 */
(function () {
  'use strict';

  const NM = window.NimbusMotion = window.NimbusMotion || {};
  const T  = () => NM.tokens;

  /* ─── Helpers ──────────────────────────────────────────────────── */

  function isReduced() {
    return window.matchMedia &&
           window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function isCoarsePointer() {
    return window.matchMedia &&
           window.matchMedia('(pointer: coarse)').matches;
  }

  function stOnce(trigger, start) {
    return {
      trigger,
      start: start || 'top 82%',
      toggleActions: 'play none none none',
      once: true
    };
  }

  /* Walk text nodes, wrap each word in <span class="nm-w"><span class="nm-w__i">…</span></span>.
   * Preserves nested elements (e.g. <em>) by recursing into them. */
  function splitWords(el) {
    if (el._nmSplit) {
      return [...el.querySelectorAll('.nm-w__i')];
    }
    const inners = [];

    const walk = (node) => {
      [...node.childNodes].forEach(child => {
        if (child.nodeType === 3) {
          /* TEXT_NODE */
          const text = child.nodeValue;
          if (!text || !text.trim()) return;
          const frag = document.createDocumentFragment();
          /* Split keeping whitespace tokens between words */
          text.split(/(\s+)/).forEach(part => {
            if (!part) return;
            if (/^\s+$/.test(part)) {
              frag.appendChild(document.createTextNode(part));
            } else {
              const wrap  = document.createElement('span');
              const inner = document.createElement('span');
              wrap.className  = 'nm-w';
              inner.className = 'nm-w__i';
              inner.textContent = part;
              wrap.appendChild(inner);
              frag.appendChild(wrap);
              inners.push(inner);
            }
          });
          child.replaceWith(frag);
        } else if (child.nodeType === 1) {
          /* ELEMENT_NODE — recurse so we keep <em>/<br>/etc. */
          walk(child);
        }
      });
    };

    walk(el);
    el._nmSplit = true;
    return inners;
  }

  /* ─── Primitives ───────────────────────────────────────────────── */

  /**
   * wordMask(target, opts)
   *   target   {Element}  heading element
   *   opts.duration  default 0.95
   *   opts.stagger   default 0.06
   *   opts.start     default 'top 82%'
   *   opts.delay     default 0
   *   opts.from      number, yPercent start (default 110)
   *   opts.timeline  optional: GSAP timeline to add to instead of standalone tween
   *   opts.position  optional: position param when adding to timeline
   *   returns { inners, tween }
   */
  function wordMask(target, opts = {}) {
    if (!target) return { inners: [], tween: null };
    const inners = splitWords(target);
    if (!inners.length) return { inners: [], tween: null };

    if (isReduced()) {
      gsap.set(inners, { yPercent: 0, opacity: 1 });
      return { inners, tween: null };
    }

    const duration = opts.duration ?? 0.95;
    const stagger  = opts.stagger  ?? 0.06;
    const from     = opts.from     ?? 110;

    const params = {
      yPercent: 0,
      opacity:  1,
      duration,
      stagger,
      ease: 'expo.out'
    };

    /* Set initial state immediately so there's no flash */
    gsap.set(inners, { yPercent: from, opacity: 0 });

    let tween;
    if (opts.timeline) {
      tween = opts.timeline.to(inners, params, opts.position);
    } else {
      tween = gsap.to(inners, {
        ...params,
        delay: opts.delay || 0,
        scrollTrigger: stOnce(target, opts.start)
      });
    }
    return { inners, tween };
  }

  /**
   * letterTrack(target, opts)
   *   Animate letter-spacing from `from` (e.g. '0.5em') to current + opacity.
   *   Doesn't split text — just tweens the property on the element.
   */
  function letterTrack(target, opts = {}) {
    if (!target) return null;
    if (isReduced()) {
      gsap.set(target, { clearProps: 'letterSpacing,opacity' });
      return null;
    }

    const duration = opts.duration ?? 1.1;
    const from     = opts.from     ?? '0.5em';

    /* Read final letter-spacing once */
    const finalLS = window.getComputedStyle(target).letterSpacing;

    gsap.set(target, { letterSpacing: from, opacity: 0 });

    const params = {
      letterSpacing: finalLS === 'normal' ? '0em' : finalLS,
      opacity: 1,
      duration,
      ease: 'power3.out'
    };

    if (opts.timeline) {
      return opts.timeline.to(target, params, opts.position);
    }
    return gsap.to(target, {
      ...params,
      delay: opts.delay || 0,
      scrollTrigger: stOnce(target, opts.start)
    });
  }

  /**
   * imageReveal(container, opts)
   *   container = the masking wrapper (overflow:hidden assumed)
   *   inner     = the image inside; we counter-translate + scale-settle
   *   from      = clip-path inset start (default 'inset(100% 0% 0% 0%)' — wipe up)
   *   counter   = if true, scale inner from 1.15 → 1 + translate Y for parallax
   */
  function imageReveal(container, opts = {}) {
    if (!container) return null;
    const inner = opts.inner || container.querySelector('img, picture, video');

    if (isReduced()) {
      gsap.set(container, { clipPath: 'none' });
      if (inner) gsap.set(inner, { clearProps: 'all' });
      return null;
    }

    const duration = opts.duration ?? 1.4;
    const from     = opts.from     ?? 'inset(100% 0% 0% 0%)';
    const counter  = opts.counter !== false;

    gsap.set(container, { clipPath: from });
    if (inner && counter) {
      gsap.set(inner, { scale: 1.18, transformOrigin: '50% 50%' });
    }

    const tl = gsap.timeline({
      scrollTrigger: stOnce(container, opts.start || 'top 82%'),
      defaults: { ease: 'expo.out' }
    });

    tl.to(container, { clipPath: 'inset(0% 0% 0% 0%)', duration }, 0);
    if (inner && counter) {
      tl.to(inner, { scale: 1, duration: duration * 1.05 }, 0);
    }
    return tl;
  }

  /**
   * staggerDepth(items, opts)
   *   Cards/grid items reveal with cascading y + scale + alternating tiny rotate.
   *   On mobile, drop the rotation and shrink offsets.
   */
  function staggerDepth(items, opts = {}) {
    if (!items || !items.length) return null;
    if (isReduced()) {
      gsap.set(items, { clearProps: 'all' });
      return null;
    }

    const isWide = window.matchMedia('(min-width: 750px)').matches;
    const y      = opts.y       ?? (isWide ? 56 : 28);
    const scale  = opts.scale   ?? (isWide ? 0.95 : 0.97);
    const stagger= opts.stagger ?? 0.08;
    const duration = opts.duration ?? (isWide ? 1.0 : 0.7);
    const rotate = opts.rotate !== false && isWide;

    gsap.set(items, {
      y,
      scale,
      opacity: 0,
      rotation: rotate ? (i) => (i % 2 === 0 ? -0.4 : 0.4) : 0,
      transformOrigin: '50% 60%'
    });

    const trigger = opts.trigger || items[0].parentElement || items[0];
    return gsap.to(items, {
      y: 0,
      scale: 1,
      rotation: 0,
      opacity: 1,
      duration,
      stagger,
      ease: 'expo.out',
      scrollTrigger: stOnce(trigger, opts.start || 'top 80%'),
      onComplete() {
        items.forEach(el => el.style && el.style.removeProperty('will-change'));
      }
    });
  }

  /**
   * scalePop(target, opts)
   *   For numbers, badges, prices, CTAs — slight overshoot via back ease.
   */
  function scalePop(target, opts = {}) {
    if (!target) return null;
    if (isReduced()) { gsap.set(target, { clearProps: 'all' }); return null; }

    const duration = opts.duration ?? 0.7;
    const from     = opts.from     ?? 0.86;

    gsap.set(target, { scale: from, opacity: 0 });

    const params = {
      scale: 1,
      opacity: 1,
      duration,
      ease: 'back.out(1.6)'
    };

    if (opts.timeline) {
      return opts.timeline.to(target, params, opts.position);
    }
    return gsap.to(target, {
      ...params,
      delay: opts.delay || 0,
      scrollTrigger: stOnce(target, opts.start || 'top 88%')
    });
  }

  /**
   * magneticTilt(card, opts)
   *   Pointer-driven 3D tilt. Hover-only, idempotent (re-init guard).
   *   Skipped on touch devices and reduced-motion.
   *   Returns a cleanup function for context.revert().
   */
  function magneticTilt(card, opts = {}) {
    if (!card || card._nmTilt) return null;
    if (isReduced() || isCoarsePointer()) return null;

    card._nmTilt = true;

    const max     = opts.max     ?? 6;       /* deg */
    const damp    = opts.damp    ?? 0.18;    /* lerp factor */
    const lift    = opts.lift    ?? 4;       /* px */
    const inner   = opts.inner   || card.querySelector('img, .nm-tilt-inner') || null;

    let rx = 0, ry = 0, tx = 0, ty = 0;
    let targetRx = 0, targetRy = 0, targetTx = 0, targetTy = 0;
    let raf = null;
    let active = false;

    card.style.transformStyle = 'preserve-3d';
    card.style.willChange = 'transform';
    if (inner) inner.style.transform = 'translateZ(0)';

    const onMove = (e) => {
      const r = card.getBoundingClientRect();
      const px = (e.clientX - r.left) / r.width  - 0.5;  /* -0.5 → 0.5 */
      const py = (e.clientY - r.top)  / r.height - 0.5;
      targetRy =  px * max;
      targetRx = -py * max;
      targetTx =  px * lift;
      targetTy =  py * lift;
      if (!active) { active = true; loop(); }
    };
    const onLeave = () => {
      targetRx = targetRy = targetTx = targetTy = 0;
    };
    const loop = () => {
      rx += (targetRx - rx) * damp;
      ry += (targetRy - ry) * damp;
      tx += (targetTx - tx) * damp;
      ty += (targetTy - ty) * damp;
      card.style.transform =
        `perspective(900px) rotateX(${rx.toFixed(2)}deg) rotateY(${ry.toFixed(2)}deg) translate3d(${tx.toFixed(2)}px, ${ty.toFixed(2)}px, 0)`;
      if (Math.abs(rx) + Math.abs(ry) + Math.abs(tx) + Math.abs(ty) > 0.05) {
        raf = requestAnimationFrame(loop);
      } else {
        card.style.transform = '';
        active = false;
        raf = null;
      }
    };

    card.addEventListener('pointermove', onMove);
    card.addEventListener('pointerleave', onLeave);

    return () => {
      card.removeEventListener('pointermove', onMove);
      card.removeEventListener('pointerleave', onLeave);
      if (raf) cancelAnimationFrame(raf);
      card.style.transform = '';
      card.style.willChange = '';
      card._nmTilt = false;
    };
  }

  /* ─── Expose ──────────────────────────────────────────────────── */
  NM.reveals = {
    wordMask,
    letterTrack,
    imageReveal,
    staggerDepth,
    scalePop,
    magneticTilt,
    splitWords,
    stOnce,
    isReduced
  };
})();
